import {
    useCallback,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IFieldSetProps from "./type";
import type {
    FieldSetRegisteredState
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useFieldGroupContext,
    FieldSetContext
} from "../../context";
import type {
    FieldSetContextType,
    FieldSetPartKind
} from "../../type";

const FieldSet: FC<IFieldSetProps> = ({
    customTheme: customThemeProp,
    children,
    style,
    ...props
}) => {
    const groupContext = useFieldGroupContext();

    const customTheme = customThemeProp ?? groupContext?.customTheme;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        registeredState,
        setRegisteredState
    ] = useState<FieldSetRegisteredState>({});

    const registeredRef = useRef<FieldSetRegisteredState>({});

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const legendID = `${baseID}-legend`;

    const descriptionID = `${baseID}-description`;

    const {
        container: containerDynamicStyle
    } = useStyles({
        spaces
    });

    const registerPart = useCallback((part: FieldSetPartKind) => {
        registeredRef.current = {
            ...registeredRef.current,
            [part]: true
        };

        setRegisteredState(registeredRef.current);

        return () => {
            registeredRef.current = {
                ...registeredRef.current,
                [part]: false
            };

            setRegisteredState(registeredRef.current);
        };
    }, []);

    const hasDescription = Boolean(registeredState.description);

    const hasLegend = Boolean(registeredState.legend);

    const context = useMemo<FieldSetContextType>(() => ({
        hasDescription,
        descriptionID,
        registerPart,
        customTheme,
        hasLegend,
        legendID
    }), [
        descriptionID,
        hasDescription,
        registerPart,
        customTheme,
        hasLegend,
        legendID
    ]);

    const webProps = Platform.OS === "web" ? {
        "aria-describedby": hasDescription ? descriptionID : undefined,
        "aria-labelledby": hasLegend ? legendID : undefined
    } : {};

    return <FieldSetContext.Provider
        value={context}
    >
        <View
            {...props}
            {...webProps}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role="group"
        >
            {children}
        </View>
    </FieldSetContext.Provider>;
};
export default FieldSet;
